<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import dayjs from '@/lib/dayjs'
import weekdayPlugin from 'dayjs/plugin/weekday'
import EventCell from './EventCell.vue'

type WebinarEvent = {
  [key: string]: unknown
  id: number
  name: string
  coach_name: string
  start_time: string | number | Date | import('dayjs').Dayjs
  end_time: string | number | Date | import('dayjs').Dayjs
  price: number
  style?: Record<string, unknown>
}

const props = defineProps<{
  viewDate: Date
  currentDate: Date
  events: WebinarEvent[]
}>()

type EmitEvent = { [key: string]: unknown; id: number }

const emit = defineEmits<{
  (e: 'event-click', event: EmitEvent): void
}>()

const container = ref<HTMLElement | null>(null)

dayjs.extend(weekdayPlugin)

onMounted(() => {
  const stop = watch(
    () => props.events,
    events => {
      stop()
      const minHour = Math.min(...events.map(event => dayjs(event.start_time).hour() - 1))
      if (Number.isFinite(minHour) && minHour > 0) scrollToHour(minHour)
      else scrollToHour()
    },
    { immediate: true }
  )
})

const currentWeekday = computed(() => dayjs(props.currentDate).weekday())

const weekdays = computed(() => {
  const result: Array<{ label: string; number: string; isCurrent: boolean }> = []
  for (let i = 0; i < 7; i++) {
    result.push({
      label: dayjs(props.viewDate).weekday(i).format('dddd'),
      number: dayjs(props.viewDate).weekday(i).format('D'),
      isCurrent: currentWeekday.value === i
    })
  }
  return result
})

const hourLabels = computed(() => {
  const result: string[] = []
  for (let i = 0; i < 24; i++) {
    result.push(dayjs().hour(i).format('HH:00'))
  }
  return result
})

const hours = computed(() => {
  const periodStart = dayjs(props.viewDate).startOf('week')
  const periodEnd = dayjs(props.viewDate).endOf('week')
  const result: Array<{ value: Date; isPast: boolean; isCurrent: boolean }> = []

  let hour = dayjs(periodStart).startOf('hour')
  while (hour.isBefore(periodEnd)) {
    result.push({
      value: hour.toDate(),
      isPast: hour.isBefore(props.currentDate, 'hour'),
      isCurrent: hour.isSame(props.currentDate, 'hour')
    })
    hour = hour.add(1, 'hour')
  }

  return result
})

const eventCells = computed(() => {
  const eventTimeVecs = props.events.map(event => [dayjs(event.start_time).unix(), dayjs(event.end_time).unix()])

  function doVecsOverlap([a1, a2]: number[], [b1, b2]: number[]) {
    const [minA, maxA] = [Math.min(a1, a2), Math.max(a1, a2)]
    const [minB, maxB] = [Math.min(b1, b2), Math.max(b1, b2)]
    return maxA >= minB && maxB >= minA
  }

  return props.events.map((event, i) => {
    const overlapsWithNext = eventTimeVecs[i + 1] && doVecsOverlap(eventTimeVecs[i], eventTimeVecs[i + 1])
    const overlapsWithPrev = eventTimeVecs[i - 1] && doVecsOverlap(eventTimeVecs[i], eventTimeVecs[i - 1])

    const rowStart = dayjs(event.start_time).hour() + 1
    const rowEnd = dayjs(event.end_time).hour() + 1

    let column: number | string = dayjs(event.start_time).weekday() + 1
    const subColumnLeft = Number(column) * 2 - 1
    const subColumnRight = Number(column) * 2 + 1

    if (overlapsWithPrev) column = subColumnRight - 1
    else if (overlapsWithNext) column = subColumnLeft
    else column = `${subColumnLeft} / ${subColumnRight}`

    return {
      ...event,
      style: {
        'grid-row-start': rowStart,
        'grid-row-end': rowEnd,
        'grid-column': column
      }
    }
  })
})

function scrollToHour(hour = 8) {
  if (!container.value) return
  const containerHeight = container.value.scrollHeight
  const headerHeight = 58
  const hourRowHeight = (containerHeight - headerHeight) / 24
  container.value.scrollTop = hourRowHeight * hour
}

function capitalize(value: string) {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}
</script>

<template>
  <div ref="container" class="webinar-calendar__view webinar-calendar__view_week">
    <div class="webinar-calendar__week-header">
      <div class="webinar-calendar__weekday">GMT +03:00</div>

      <div
        v-for="(weekday, i) in weekdays"
        :key="i"
        class="webinar-calendar__weekday"
        :class="{ 'webinar-calendar__weekday_active': weekday.isCurrent }"
      >
        <span>{{ capitalize(weekday.label) }}</span>
        <span class="webinar-calendar__weekday-number" v-text="weekday.number" />
      </div>
    </div>

    <div class="webinar-calendar__week-container">
      <div class="webinar-calendar__labels-grid">
        <div v-for="(label, i) in hourLabels" :key="i" class="webinar-calendar__hour-label">
          {{ capitalize(label) }}
        </div>
      </div>

      <div class="webinar-calendar__week-main-grid">
        <div class="webinar-calendar__week-background-grid">
          <div
            v-for="hour in hours"
            :key="hour.value.toJSON()"
            class="webinar-calendar__hour-cell"
            :class="{ 'webinar-calendar__hour-cell_past': hour.isPast }"
          >
            <svg v-if="hour.isCurrent" class="webinar-calendar__hour-cell-track">
              <line x1="0" y1="5" x2="100%" y2="5" stroke-width="2" />
              <circle cx="4.5" cy="5" r="4.5" />
            </svg>
          </div>
        </div>

        <div class="webinar-calendar__week-events-grid">
          <EventCell
            v-for="event in eventCells"
            :key="event.id"
            class="webinar-calendar__week-event-cell"
            :event="event"
            :style="event.style"
            @event-click="e => emit('event-click', e)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use 'sass:color';

.webinar-calendar {
  &__view_week {
    display: flex;
    flex-direction: column;
    overflow-x: auto;
    padding: 1px;
  }

  // Header
  &__week-header {
    display: grid;
    grid-template-columns: 56px repeat(7, minmax(100px, 1fr));

    position: sticky;
    top: -1px;
    z-index: 2;
    background: white;
  }

  &__weekday {
    flex: 1 0 auto;
    padding: 8px 8px 6px;

    display: flex;
    flex-direction: column;

    @include text-caption;

    border: 1px $lightGray solid;
    &:not(:last-child) {
      border-right: none;
    }

    &:first-child {
      justify-content: flex-end;
      text-align: center;
    }

    &_active {
      padding: 6px 8px 6px;
      border-top: 2px $blue solid;
      color: $blue;
    }
  }

  &__weekday-number {
    @include text-body-bold;
    margin-top: 2px;
  }

  // Content
  &__week-container {
    flex: 1;
    margin: -1px 0 0;

    display: grid;
    grid-template-columns: 56px minmax(700px, 7fr);
    border-bottom: 1px $lightGray solid;
  }

  // Labels (at the left)
  &__labels-grid {
    display: grid;
    grid-template-rows: repeat(24, 70px);
  }

  &__hour-label {
    @include text-caption;
    color: $darkGray;
    border-left: 1px $lightGray solid;

    display: flex;
    justify-content: center;
    align-items: center;
  }

  // Values (main events grid)
  &__week-main-grid {
    position: relative;
  }

  &__week-events-grid,
  &__week-background-grid {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;

    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: repeat(24, 70px);
    grid-auto-flow: column;
  }

  &__week-events-grid {
    z-index: 1;
    grid-template-columns: repeat(14, 1fr);
  }

  &__week-event-cell {
    height: 100%;
  }

  &__hour-cell {
    border: 1px color.adjust($lightGray, $alpha: -0.4) solid;
    margin: 0 0 -1px 0;

    padding: 4px;

    display: flex;
    flex-direction: column;
    position: relative;

    &_disabled {
      color: $gray;
    }

    &_today {
      background: $lightenBlue;
      border-top: 3px $blue solid;
      color: $blue;
    }

    &_past {
      background: $lightenGray;
    }
  }

  &__hour-cell-track {
    width: calc(100% + 8px);
    height: 10px;
    position: absolute;
    top: -6px;
    left: -8px;
    z-index: 2;

    fill: $red;
    stroke: $red;
  }
}
</style>
